import { Link, useNavigate } from "react-router-dom";
import {
  Breadcrumb,
  Card,
  Form,
  Row,
  Col,
  Button
} from "react-bootstrap";
import {
  Page,
  Spinner,
  IconButton,
  IconButtonType,
  Divider,
  ErrorMessages
} from "@whitespace/whitespace-ui";
import { ariaInvalidCheck } from "@whitespace/whitespace-utils";
import { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import AmplifyError from "components/AmplifyError/AmplifyError";
import hwrcSiteSchema from "modules/schemas/hwrcSiteSchema";
import IAddHwrcSiteRequestModel from "interfaces/Request/IAddHwrcSiteRequestModel";
import IRegion from "interfaces/IRegion";

import "styles/form.scss";
import "styles/form-mixins.scss";
import { normaliseLatLong } from "modules/Normalise";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";

const AddHwrcSite = () => {
  const [showExceptions, setShowExceptions] = useState<boolean>(false);
  const [addButtonDisabled, setAddButtonDisabled] = useState<boolean>(true);
  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues
  } = useForm<IAddHwrcSiteRequestModel>({
    resolver: yupResolver(hwrcSiteSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const navigate = useNavigate();

  const {
    isSending: getRegionsIsSending,
    errors: getRegionsErrors,
    data: getRegionsData,
    sendRequest: getRegionsSendRequest
  } = useAmplifyREST();

  const {
    isSending: addHwrcSiteIsSending,
    errors: addHwrcSiteErrors,
    statusCode: addHwrcSiteStatusCode,
    sendRequest: addHwrcSiteSendRequest
  } = useAmplifyREST();

  const isSending = getRegionsIsSending || addHwrcSiteIsSending;
  const apiErrors = getRegionsErrors || addHwrcSiteErrors;

  useEffect(() => {
    getRegionsSendRequest("settings/regions", "GET");
  }, [getRegionsSendRequest]);

  useEffect(() => {
    if (apiErrors) setShowExceptions(true);
  }, [apiErrors]);

  const canFormBeSubmitted = () => {
    setAddButtonDisabled(
      !((getValues("name").length > 0)
        && (getValues("code").length > 0)
        && (getValues("regionId").length > 0)
      )
    );
  };

  const handleAddHwrcSiteClick = (request: IAddHwrcSiteRequestModel) => {
    request.latitude = request.latitude!.length > 0 ? request.latitude : undefined;
    request.longitude = request.longitude!.length > 0 ? request.longitude : undefined;
    addHwrcSiteSendRequest("settings/hwrcsites", "POST", request);
  };

  useEffect(() => {
    if (addHwrcSiteStatusCode === 200) navigate(RoutePathsEnum.SettingsHwrcSites);
  }, [addHwrcSiteStatusCode]);

  const renderRegions = () => {
    const regions = [
      <option key="" value="">Please Select</option>
    ];
    if (getRegionsData) {
      (getRegionsData.regions as IRegion[]).forEach(region => {
        regions.push(<option key={region.id} value={region.id}>{region.name}</option>);
      });
    }

    return regions;
  };

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="hwrc-title">
              Add HWRC Site
              <Spinner
                animation="grow"
                visible={isSending}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
        </Card.Header>
        <Form autoComplete="off" onSubmit={handleSubmit(handleAddHwrcSiteClick)}>
          <Card.Body className="no-padding">
            <Row className="d-lg-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb className="left-margin-large no-wrap">
                  <Breadcrumb.Item
                    data-testid="breadcrumb-settings"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.Settings }}
                    onClick={() => navigate(RoutePathsEnum.Settings)}
                  >
                    Settings
                  </Breadcrumb.Item>
                  <Breadcrumb.Item
                    data-testid="breadcrumb-hwrcsites"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.SettingsHwrcSites }}
                    onClick={() => navigate(RoutePathsEnum.SettingsHwrcSites)}

                  >
                    HWRC Sites
                  </Breadcrumb.Item>
                  <Breadcrumb.Item data-testid="breadcrumb-addhwrcsite" active>
                    Add HWRC Site
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>
              <Col className="d-lg-flex justify-content-end user-filter">
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={addButtonDisabled || isSending}
                  aria-disabled={addButtonDisabled || isSending}
                  aria-label="Create HWRC Site"
                  type="submit"
                  spin={addHwrcSiteIsSending}
                  data-testid="create"
                >
                  Create
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={() => navigate(RoutePathsEnum.SettingsHwrcSites)}
                  data-testid="cancel"
                  aria-label="Cancel creation and back to HWRC Sites"
                >
                  Cancel
                </Button>
              </Col>
            </Row>
            <Divider />
            <section className="hwrcsites pt-2">
              <Row className="d-flex mt-1 ms-1">
                <Col xs={12}>
                  <div className="control-pane">
                    <div className="control-section" />
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="name">
                        <Form.Label id="siteNameLabel" className="horiz-form-label">
                          Site Name:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required="true"
                            aria-invalid={ariaInvalidCheck("name", errors, null)}
                            aria-labelledby="siteNameLabel"
                            maxLength={50}
                            onKeyUp={() => canFormBeSubmitted()}
                            {...register("name")}
                          />
                        </Col>
                        <ErrorMessages fieldName="name" errors={errors} validationFailures={null} />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="code">
                        <Form.Label id="siteCodeLabel" className="horiz-form-label">
                          Site Code (must align with mobile system):
                        </Form.Label>
                        <Col>
                          <Form.Control
                            {...register("code")}
                            type="text"
                            role="textbox"
                            aria-required="true"
                            aria-invalid={ariaInvalidCheck("code", errors, null)}
                            aria-labelledby="siteCodeLabel"
                            maxLength={50}
                            onKeyUp={() => canFormBeSubmitted()}
                          />
                        </Col>
                        <ErrorMessages fieldName="code" errors={errors} validationFailures={null} />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="regionId">
                        <Form.Label id="regionLabel" className="horiz-form-label">
                          Region:
                        </Form.Label>
                        <Col>
                          <Form.Select
                            {...register("regionId", { onChange: () => canFormBeSubmitted() })}
                            data-testid="ddlRegion"
                            aria-required
                            aria-invalid={false}
                            aria-labelledby="regionLabel"
                          >
                            {renderRegions()}
                          </Form.Select>
                        </Col>
                        <ErrorMessages fieldName="regionId" errors={errors} validationFailures={null} />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="latitude">
                        <Form.Label id="latitudeLabel" className="horiz-form-label">
                          Latitude:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            {...register("latitude")}
                            type="text"
                            role="textbox"
                            aria-required="false"
                            aria-invalid={ariaInvalidCheck("latitude", errors, null)}
                            aria-labelledby="latitudeLabel"
                            maxLength={50}
                            onChange={e => { e.target.value = normaliseLatLong(e.target.value); }}

                          />
                        </Col>
                        <ErrorMessages fieldName="latitude" errors={errors} validationFailures={null} />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="longitude">
                        <Form.Label id="longitudeLabel" className="horiz-form-label">
                          Longitude:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            {...register("longitude")}
                            type="text"
                            role="textbox"
                            aria-required="false"
                            aria-invalid={ariaInvalidCheck("longitude", errors, null)}
                            aria-labelledby="longitudeLabel"
                            maxLength={50}
                            onChange={e => { e.target.value = normaliseLatLong(e.target.value); }}

                          />
                        </Col>
                        <ErrorMessages fieldName="longitude" errors={errors} validationFailures={null} />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2 mt-3">
                      <Form.Group as={Row} controlId="active" className="d-flex align-items-center">
                        <Form.Label id="activeLabel" className="d-inline-flex horiz-form-label w-auto mb-0 me-3">
                          Active:
                        </Form.Label>
                        <Form.Check
                          {...register("active")}
                          type="switch"
                          className="d-inline-flex custom-switch custom-switch-md ms-2 w-auto"
                          aria-required="true"
                          aria-labelledby="activeLabel"
                          defaultChecked
                          onKeyUp={() => canFormBeSubmitted()}
                        />
                        <ErrorMessages fieldName="active" errors={errors} validationFailures={null} />
                      </Form.Group>
                    </Row>
                  </div>
                </Col>
              </Row>
            </section>
          </Card.Body>
        </Form>

      </Card>
    </Page>
  );
};

export default AddHwrcSite;
